import { ChevronDownIcon, ChevronRightIcon, EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';

import chartImage from 'assets/blox-model.png';
import { Block } from 'models/response/model.interface';
import colors from 'styles/theme/colors';
import sizes from 'styles/theme/sizes';
import {
  DeleteUserModels,
  DuplicateUserModels,
  getModels,
  GetUserModels,
  RenameUserModels,
  StartDuplicateModel,
  StopDuplicateModel,
} from 'redux/ModelsSlice';
import BoxShimmer from 'components/FullScreenLoader/BoxShimmer';
import { getUserSubscriptionLimits, SetModelLimit } from 'redux/UserSlice';
import PlanPermission from 'container/PlanPermission';
import DeleteModal from 'components/Modals/DeleteModal';
import InfoIconComponent from 'components/Icons/InfoIcon';

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

interface Properties {
  model: any;
  isListMode?: boolean;
  planPermission: boolean;
  handleProperties?: (data: any) => void;
}

const DesktopModel = ({
  model,
  planPermission,
  isListMode = false,
  handleProperties,
}: Properties) => {
  const navigate = useNavigate();
  const [isMiniMac] = useMediaQuery('(max-height: 700px)');
  const [isLaptop] = useMediaQuery('(max-height: 1200px)');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [menu, setMenu]: any = useState('');
  const { isOpen, onClose, onToggle } = useDisclosure();

  const models = useSelector(getModels);

  const dispatch: AppDispatch = useDispatch();

  const onCardClick = () => {
    navigate(`/model/${model.id}/home`);
  };
  const permissionData = useSelector(getUserSubscriptionLimits);

  const onStackedCardClick = (event: any) => {
    event.stopPropagation();
    if (!isEditMode) {
      if (model.blocks.length > 1) {
        onToggle();
        return;
      }
      navigate(`/model/${model.id}/home`);
    }
  };

  const onOpenEditMode = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsEditMode((previousState) => !previousState);
  };

  const onBlur = () => {
    setIsEditMode(false);
  };

  const onOpenDeleteModal = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteModal(true);
  };

  const onDeleteModel = async () => {
    setIsDeleteModal(false);
    await dispatch(DeleteUserModels({ modelId: model.id }));
    dispatch(GetUserModels({}));
  };

  const onRenameModel = async (value: string) => {
    setIsEditMode(false);
    await dispatch(
      RenameUserModels({
        model_id: model.id,
        request_body: { name: value },
        toaster: true,
      }),
    );
    dispatch(GetUserModels({}));
  };

  const onMenuItemClick = (block: Block) => {
    if (!isEditMode) {
      navigate(`/model/${model.id}/block/${block.id}/plan`);
    }
  };

  const copyModel = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (
      permissionData?.subscription_features_limit?.No_of_models &&
      !(permissionData?.subscription_features_limit?.No_of_models > models?.length)
    ) {
      dispatch(SetModelLimit(true));
    } else {
      dispatch(
        StartDuplicateModel({
          id: '',
          name: '',
        }),
      );
      dispatch(
        DuplicateUserModels({
          modelId: model?.id,
          modelName: { name: `${model?.name} - Copy`, all_models_data: true },
          toaster: true,
        }),
      ).then(() => {
        dispatch(StopDuplicateModel());
      });
    }
  };

  const handleMenuClick = (event: any) => {
    event.stopPropagation();
  };

  return (
    <Box marginTop='0 !important'>
      {model?.id ? (
        <Flex
          direction='column'
          position='relative'
          alignItems='center'
          w='fit-content'
          h='fit-content'
          justifyContent='center'
        >
          <Flex
            position='relative'
            padding='0 !important'
            direction='column'
            w='270px'
            minH={isListMode ? '70px' : '215px'}
            align='center'
            justify='space-between'
            py={isListMode ? '2' : '4'}
            px='2'
            pb={1}
            borderRadius='sm'
            borderWidth='1'
            borderColor='grey.900'
            bg='white.900'
            cursor='pointer'
            zIndex={model?.id === menu ? 4 : 3}
            onClick={!isEditMode ? onCardClick : undefined}
          >
            {!isListMode && (
              <Image
                style={{
                  height: '130px',
                  width: '100%',
                  borderRadius: '10px 10px 0 0',
                  objectFit: 'contain',
                }}
                src={model.icon || chartImage}
                mb='2'
              />
            )}

            <Flex direction='column' w='full' flex='1' padding={'5px 10px'}>
              <Flex w='full'>
                {isEditMode ? (
                  <Editable
                    w='full'
                    defaultValue={model.name}
                    onSubmit={(event) => {
                      if (event.trim() !== '') onRenameModel(event);
                    }}
                    startWithEditView={isEditMode}
                    onBlur={onBlur}
                    mb='2'
                    h='6'
                    fontFamily='bold_roboto'
                    fontSize='11px'
                    lineHeight='13px'
                    color='black.900'
                  >
                    <EditablePreview w='full' />
                    <EditableInput
                      _focusVisible={{
                        h: 'full',
                        px: '1',
                        borderWidth: '3',
                        borderRadius: 'xs',
                        borderColor: 'blue.500',
                      }}
                      required
                    />
                  </Editable>
                ) : (
                  <Tooltip label={model.name} borderRadius='4' fontSize='3xs'>
                    <Text
                      variant='secondary'
                      mb='2'
                      fontFamily='bold_roboto'
                      fontSize='11px'
                      lineHeight='13px'
                      noOfLines={2}
                      w='full'
                    >
                      {model.name}
                    </Text>
                  </Tooltip>
                )}
                <Menu
                  closeOnSelect={true}
                  onOpen={() => setMenu(model?.id)}
                  onClose={() => setMenu('')}
                  variant='primary'
                  placement='bottom-end'
                >
                  <MenuButton onClick={(event: any) => handleMenuClick(event)}>
                    <InfoIconComponent color='#BDBCD0' />
                  </MenuButton>
                  <MenuList minWidth={'160px !important'} zIndex={5}>
                    <MenuItem onClick={onOpenEditMode} isDisabled={isEditMode || planPermission}>
                      Rename
                    </MenuItem>
                    <MenuItem onClick={copyModel} isDisabled={planPermission}>
                      Duplicate
                    </MenuItem>
                    <MenuItem onClick={onOpenDeleteModal} isDisabled={planPermission}>
                      Delete
                    </MenuItem>
                    <MenuItem
                      onClick={(event: any) => {
                        event.stopPropagation();
                        if (handleProperties)
                          handleProperties({
                            id: model?.id,
                            name: model?.name,
                            description: model?.description,
                            icon: model?.icon,
                            createdAt: model?.date_created,
                            ownerImage: model?.owner?.profile_image_url,
                            ownerName: `${model?.owner?.firstname} ${model?.owner?.lastname}`,
                          });
                      }}
                      isDisabled={planPermission}
                    >
                      Properties
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>

              <Flex justify='space-between' alignItems='flex-start' align='flex-end' mt='auto'>
                <Text
                  fontFamily='regular_roboto'
                  fontSize='11px'
                  lineHeight='13px'
                  color='grey.900'
                >
                  {` Created: ${dayjs(model.date_created).format('D MMM, YYYY')}`}
                </Text>
              </Flex>
            </Flex>
            <Divider w='270px' borderColor='grey' mt={sizes[1]} mb={sizes[1]} />
            <Flex
              direction='row'
              justify='space-between'
              align='flex-end'
              mt='auto'
              onClick={onStackedCardClick}
              w='full'
              p={sizes[1]}
            >
              <Menu isOpen={isOpen} onClose={onClose} strategy='fixed'>
                <MenuButton
                  as={Button}
                  rightIcon={model.blocks.length > 1 ? <ChevronDownIcon /> : <></>}
                  color={colors.black[900]}
                  p={0}
                  m={0}
                  w='fit-content'
                  h='fit-content'
                  minW='auto'
                  textTransform='none'
                  fontWeight='normal'
                >
                  {`${model.blocks.length} ${model.blocks.length > 1 ? 'Blocks' : 'Block'}`}
                </MenuButton>
                <Flex direction='row' columnGap={sizes['0.5']} overflow='hidden'>
                  {model.blocks.map((_: any, index: number) => (
                    <Icon viewBox='0 0 200 200' color='#4bb6b9' key={index}>
                      <path
                        fill='currentColor'
                        d='M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0'
                      />
                    </Icon>
                  ))}
                </Flex>
                {isOpen && (
                  <Portal>
                    <MenuList
                      className='scroll'
                      overflowY='auto'
                      color={colors.black[900]}
                      minW='270px'
                      left={`-${sizes[3]}`}
                      top={sizes[4]}
                      borderRadius='sm'
                      maxHeight={isMiniMac ? '160px' : isLaptop ? '200px' : `calc(100vh - 55vh)`}
                      scrollBehavior='smooth'
                    >
                      {model.blocks?.map((block: any) => (
                        <MenuItem
                          display='flex'
                          icon={<EditIcon verticalAlign='text-top' />}
                          alignItems='center'
                          key={block.id}
                          justifyContent='space-between'
                          onClick={() => onMenuItemClick(block)}
                        >
                          <Box width='100%' display='flex'>
                            <Text as='span' fontSize='xs'>
                              {block.name}
                            </Text>
                            <Text as='span' ml='auto'>
                              <ChevronRightIcon textColor='#4bb6b9' fontSize='lg' />
                            </Text>
                          </Box>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Portal>
                )}
              </Menu>
            </Flex>
          </Flex>
          {model.blocks.length > 1 && (
            <>
              <Box
                w='264px'
                h='16px'
                mt='-12px'
                borderBottomRadius='xl'
                borderWidth='1'
                borderColor='grey.900'
                bg='white.900'
                zIndex={2}
              />
              <Box
                w='248px'
                h='16px'
                mt='-12px'
                borderBottomRadius='xl'
                borderWidth='1'
                borderColor='grey.900'
                bg='white.900'
                zIndex={1}
              />
            </>
          )}
        </Flex>
      ) : (
        <BoxShimmer
          width='270px'
          minH={isListMode ? '70px' : '215px'}
          height={isListMode ? '82.75px' : '220px'}
          isList={isListMode}
        />
      )}

      {isDeleteModal && (
        <DeleteModal
          isOpen={isDeleteModal}
          onClose={() => setIsDeleteModal(false)}
          itemName={model.name}
          onDelete={onDeleteModel}
        />
      )}
    </Box>
  );
};

export default PlanPermission(DesktopModel);
